import React, { useState } from 'react';
import { useStandardAreaManagement } from '../hooks/useStandardAreaManagement';
import { SaveStandardAreaRequest, StandardAreaDto } from '../types/standardArea.types';
import StandardAreaModal from './StandardAreaModal';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import '../styles/StandardAreaManagementPage.css';

const MAX_VISIBLE_SUPPLIERS = 3;
const PAGE_SIZE = 15;

const StandardAreaManagementPage: React.FC = () => {
    const { areas, loading, saveArea, deleteArea, autoRegister } = useStandardAreaManagement();

    const [nameFilter, setNameFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<StandardAreaDto | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<StandardAreaDto | null>(null);
    const [autoRegisterConfirm, setAutoRegisterConfirm] = useState(false);

    const handleSave = async (req: SaveStandardAreaRequest) => {
        const success = await saveArea(req);
        if (success) {
            setModalOpen(false);
            setCurrentPage(1);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        const success = await deleteArea(deleteTarget.seqNo);
        if (success) setDeleteTarget(null);
    };

    const handleAutoRegister = async () => {
        setAutoRegisterConfirm(false);
        await autoRegister();
    };

    const filteredAreas = nameFilter.trim()
        ? areas.filter(a => a.areaName.includes(nameFilter.trim()))
        : areas;
    const totalPages = Math.ceil(filteredAreas.length / PAGE_SIZE);
    const pagedAreas = filteredAreas.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div className="sa-page-content">
            <div className="sa-page-header">
                <h1 className="sa-page-title">표준권역 관리</h1>
                <div className="sa-header-actions">
                    <input
                        type="text"
                        className="sa-search-input"
                        value={nameFilter}
                        onChange={e => { setNameFilter(e.target.value); setCurrentPage(1); }}
                        placeholder="표준권역명 검색"
                    />
                    <button className="sa-btn-auto" onClick={() => setAutoRegisterConfirm(true)}>
                        기본설정값 등록
                    </button>
                    <button
                        className="sa-btn-add"
                        onClick={() => { setEditTarget(null); setModalOpen(true); }}
                    >
                        + 권역 추가
                    </button>
                </div>
            </div>

            <div className="sa-result-count">
                <i className="fas fa-list" />
                총 <strong>{filteredAreas.length}</strong>개의 표준권역
            </div>

            <div className="sa-table-wrap">
                <table className="sa-table">
                    <thead>
                    <tr>
                        <th style={{ width: 60 }}>No.</th>
                        <th className="text-left" style={{ width: 180 }}>표준권역명</th>
                        <th className="text-left">소속 협력사</th>
                        <th style={{ width: 120 }}>등록일</th>
                        <th style={{ width: 140 }}>관리</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={5} className="sa-empty">조회 중...</td>
                        </tr>
                    ) : filteredAreas.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="sa-empty">
                                {nameFilter.trim() ? '검색 결과가 없습니다.' : '등록된 표준권역이 없습니다.'}
                            </td>
                        </tr>
                    ) : (
                        pagedAreas.map((area, idx) => (
                            <tr key={area.seqNo}>
                                <td>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                <td className="text-left sa-area-name">{area.areaName}</td>
                                <td className="text-left">
                                    {area.suppliers.length === 0 ? (
                                        <span className="sa-supplier-none">없음</span>
                                    ) : (
                                        <div className="sa-supplier-list">
                                            {area.suppliers
                                                .slice(0, MAX_VISIBLE_SUPPLIERS)
                                                .map((s) => (
                                                    <span key={s.suppId} className="sa-supplier-tag">
                                                            {s.suppName || s.suppId}
                                                        </span>
                                                ))}
                                            {area.suppliers.length > MAX_VISIBLE_SUPPLIERS && (
                                                <span className="sa-supplier-more">
                                                        +{area.suppliers.length - MAX_VISIBLE_SUPPLIERS}개 더
                                                    </span>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td>{area.insDate ?? '-'}</td>
                                <td>
                                    <button
                                        className="sa-btn-edit"
                                        onClick={() => { setEditTarget(area); setModalOpen(true); }}
                                    >
                                        수정
                                    </button>
                                    <button
                                        className="sa-btn-delete"
                                        onClick={() => setDeleteTarget(area)}
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            <StandardAreaModal
                isOpen={modalOpen}
                editTarget={editTarget}
                onSave={handleSave}
                onClose={() => setModalOpen(false)}
            />

            <ConfirmModal
                isOpen={!!deleteTarget}
                title="표준권역 삭제"
                message={deleteTarget ? `"${deleteTarget.areaName}" 권역을 삭제하시겠습니까?\n소속 협력사 매핑도 함께 삭제됩니다.` : ''}
                onConfirm={() => { void handleDeleteConfirm(); }}
                onCancel={() => setDeleteTarget(null)}
            />

            <ConfirmModal
                isOpen={autoRegisterConfirm}
                title="기본설정값 등록"
                message={`협력사명에서 "부릉" 이전 문자열을 추출하여 표준권역을 자동 등록합니다.\n이미 등록된 권역/매핑은 건너뜁니다. 진행하시겠습니까?`}
                onConfirm={() => { void handleAutoRegister(); }}
                onCancel={() => setAutoRegisterConfirm(false)}
            />
        </div>
    );
};

export default StandardAreaManagementPage;
